/**
 * apiError — always return a **string** from any FastAPI / Axios error.
 *
 * FastAPI validation (422) puts a LIST of objects in `detail`:
 *   { detail: [ { type, loc, msg, input, url }, ... ] }
 *
 * FastAPI raise HTTPException(status, "message") puts a STRING:
 *   { detail: "Invalid credentials" }
 *
 * If we pass the array/object straight to `toast.error()` or render it as
 * a React child, React throws "Objects are not valid as a React child"
 * (production error #31) — a bug that repeatedly crashed the login page
 * on some browsers.  Never pass anything but a string to a rendering call.
 */
export function apiError(err, fallback = "Something went wrong. Please try again.") {
  // Network or CORS failure — no response at all
  if (!err?.response) {
    return err?.message || fallback;
  }
  const data = err.response.data;

  // Some endpoints return a plain string body
  if (typeof data === "string") return data;

  const detail = data?.detail;

  // detail: string
  if (typeof detail === "string") return detail;

  // detail: Pydantic 422 — array of { loc, msg, type, ... }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (!d || typeof d !== "object") return "";
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        const msg = typeof d.msg === "string" ? d.msg : "";
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : fallback;
  }

  // detail: single object
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }

  // Some backends put the message under `message`
  if (typeof data?.message === "string") return data.message;

  return fallback;
}
